import * as React from 'react';
import { PanelMarker, FurtherConsiderations, Section } from '../../models';

type Props = {
  section: Section;
  panelMarkers: PanelMarker[];
  furtherConsiderations: FurtherConsiderations;
};

// The "About" tab lists a plain-language description for every marker in the
// section that has a value, regardless of where that value falls in the ranges.
// `panelMarkers` is already filtered to markers-with-values upstream (the
// per-section getters in BloodworkAnalysis), so a marker appearing here means
// the client has a result for it. We render the description authored for that
// marker (resolved via FurtherConsiderations.findAboutConsideration), keeping
// the same order the markers appear in elsewhere in the section.
export class AboutSection extends React.Component<Props, {}> {
  render() {
    const { section, panelMarkers, furtherConsiderations } = this.props;

    const shown = (panelMarkers || []).filter(
      pm => pm && pm.field && (!pm.hasValue || pm.hasValue()),
    );
    const entries = shown
      .map(pm => ({
        about: furtherConsiderations.findAboutConsideration(
          section,
          pm.field!.id,
        ),
      }))
      .filter(e => e.about && e.about.items && e.about.items.length > 0);

    // Some markers are unit variants of the same analyte (e.g. Copper, RBC in
    // μg/mL and μg/dL) and resolve to a single About row — show each
    // description once.
    const seen: { [categoryId: number]: boolean } = {};
    const unique = entries.filter(e => {
      const id = e.about!.categoryId;
      if (seen[id]) return false;
      seen[id] = true;
      return true;
    });

    // A section-wide note (e.g. Hormones' "About Blood Hormone Levels") goes
    // once at the top whenever the section shows any marker.
    const note = shown.length
      ? furtherConsiderations.findSectionAboutNote(section)
      : undefined;
    const all = note ? [{ about: note }, ...unique] : unique;

    if (all.length === 0) {
      return (
        <p className="text-muted">
          No marker descriptions are available for this section.
        </p>
      );
    }

    return (
      <span>
        {all.map((e, idx) => (
          <span key={idx}>
            <span className="consideration-title">{e.about!.title}</span>
            <ul>
              {e.about!.items.map((item, itemIndex) => (
                <li key={`${e.about!.categoryId}-${itemIndex}`}>{item}</li>
              ))}
            </ul>
          </span>
        ))}
      </span>
    );
  }
}
